import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Eye, XCircle } from "lucide-react";
import { rpc } from "@/lib/supabase";
import { PageHeader, Loader, Card } from "@/components/kit";
import { AdminTable } from "@/components/AdminTable";
import { StatusBadge } from "@/components/StatusBadge";
import { formatCOP, formatDateTime } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const fmtElapsed = (min) => {
  const m = Math.round(Number(min || 0));
  return m >= 120 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
};

const ClassBadge = ({ value }) =>
  value === "PRUEBA" ? (
    <span className="rounded-full border border-amber-500/50 bg-amber-500/10 px-2.5 py-1 text-xs font-bold text-amber-400" data-testid="classification-badge">Prueba</span>
  ) : (
    <span className="rounded-full border border-gray-500/50 bg-gray-500/10 px-2.5 py-1 text-xs font-bold text-gray-300" data-testid="classification-badge">Sin clasificar</span>
  );

export default function AdminStuckServices() {
  const [rows, setRows] = useState(null);
  const [threshold, setThreshold] = useState(30);
  const [applied, setApplied] = useState(30);
  const [audit, setAudit] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    rpc("admin_get_stuck_services", { p_threshold_minutes: applied })
      .then((d) => setRows(d || []))
      .catch(() => setRows([]));
  }, [applied]);

  useEffect(() => {
    load();
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, [load]);

  const openAudit = async (id) => {
    setAudit(null);
    setAuditOpen(true);
    try {
      setAudit(await rpc("admin_get_service_audit", { p_service_id: id }));
    } catch (e) {
      toast.error(e?.message || "Error");
      setAuditOpen(false);
    }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      await rpc("cancel_service", {
        p_service_id: cancelTarget.id,
        p_reason: `[ADMIN · Panel atascados] ${reason.trim()}`,
      });
      toast.success("Servicio cancelado y registrado en el historial");
      setCancelTarget(null);
      setReason("");
      setConfirmed(false);
      load();
    } catch (e) {
      toast.error(e?.message || "Error");
    } finally {
      setBusy(false);
    }
  };

  // El más antiguo primero (el RPC ordena por created_at asc); desempate determinista por id.
  const topId = rows?.length
    ? rows.reduce((a, b) => (a.created_at < b.created_at || (a.created_at === b.created_at && a.id < b.id) ? a : b)).id
    : null;

  const columns = [
    { key: "id", label: "ID", render: (r) => (
        <div className="flex flex-col items-start gap-1">
          <span className="font-mono text-xs">#{r.id.slice(0, 8)}</span>
          {r.id === topId && (
            <span className="rounded-full border border-[#FF6A00] bg-[#FF6A00]/15 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[#FF6A00]" data-testid="most-stuck-badge">
              Mayor tiempo atascado
            </span>
          )}
        </div>
      ) },
    { key: "classification", label: "Clasificación", render: (r) => <ClassBadge value={r.classification} /> },
    { key: "business_name", label: "Negocio", render: (r) => r.business_name || r.business_owner || "—" },
    { key: "route", label: "Ruta", render: (r) => (
        <div className="max-w-[220px]">
          <div className="truncate text-white">{r.pickup_address}</div>
          <div className="truncate text-xs text-gray-500">→ {r.dropoff_address}</div>
        </div>
      ) },
    { key: "created_at", label: "Creado", render: (r) => <span className="text-xs">{formatDateTime(r.created_at)}</span> },
    { key: "minutes_elapsed", label: "Transcurrido", render: (r) => <span className="font-semibold text-amber-400" data-testid="elapsed-cell">{fmtElapsed(r.minutes_elapsed)}</span> },
    { key: "offers_count", label: "Ofertas", render: (r) => <span className="font-semibold" data-testid="offers-cell">{r.offers_count}</span> },
    { key: "last_activity", label: "Última actividad", render: (r) => <span className="text-xs">{formatDateTime(r.last_activity)}</span> },
    { key: "actions", label: "", render: (r) => (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="border-[#27272A] text-gray-300" onClick={() => openAudit(r.id)} data-testid={`stuck-detail-${r.id}`}>
            <Eye size={14} className="mr-1" /> Detalle
          </Button>
          <Button size="sm" className="bg-red-500/90 text-white hover:bg-red-600" onClick={() => setCancelTarget(r)} data-testid={`stuck-cancel-${r.id}`}>
            <XCircle size={14} className="mr-1" /> Cancelar
          </Button>
        </div>
      ) },
  ];

  return (
    <div>
      <PageHeader
        title="Servicios atascados"
        subtitle="Envíos en búsqueda de conductor que superan el umbral. Detección informativa: nada se cancela automáticamente."
        testId="admin-stuck"
        action={
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Umbral (min)</span>
            <Input
              type="number" min="0" value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="w-24 border-[#27272A] bg-[#0d0d0f]"
              data-testid="threshold-input"
            />
            <Button
              variant="outline" className="border-[#FF6A00] text-[#FF6A00]"
              onClick={() => setApplied(Math.max(0, Number(threshold) || 0))}
              data-testid="threshold-apply"
            >
              Aplicar
            </Button>
          </div>
        }
      />
      {rows === null ? <Loader /> : (
        <AdminTable
          columns={columns}
          rows={rows}
          testId="admin-stuck-table"
          empty={`Ningún servicio supera ${applied} min en búsqueda de conductor`}
          rowClassName={(r) => (r.id === topId ? "border-l-4 border-l-[#FF6A00] bg-[#FF6A00]/5" : "")}
        />
      )}

      {/* Detalle / auditoría */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-[#27272A] bg-[#141416] text-white sm:max-w-2xl" data-testid="stuck-audit-dialog">
          <DialogHeader>
            <DialogTitle>Auditoría del servicio {audit ? `#${audit.service.id.slice(0, 8)}` : ""}</DialogTitle>
            <DialogDescription>Historial de estados y ofertas. Solo lectura.</DialogDescription>
          </DialogHeader>
          {!audit ? <Loader /> : (
            <div className="space-y-5">
              <Card className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <StatusBadge status={audit.service.status} />
                  <span className="font-heading text-lg font-extrabold text-[#FF6A00]">{formatCOP(audit.service.quoted_price)}</span>
                </div>
                <div className="mt-2 text-sm text-gray-300">{audit.service.pickup_address}</div>
                <div className="text-xs text-gray-500">→ {audit.service.dropoff_address}</div>
              </Card>
              <div>
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-500">Historial de estados</div>
                <div className="space-y-2" data-testid="audit-history">
                  {audit.history.map((h, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 rounded-xl border border-[#1e1e21] bg-[#101012] px-3 py-2">
                      <div>
                        <StatusBadge status={h.status} />
                        {h.note && <div className="mt-1 text-xs text-gray-400">{h.note}</div>}
                      </div>
                      <div className="text-right text-xs text-gray-500">
                        <div>{formatDateTime(h.created_at)}</div>
                        <div>{h.changed_by_name || "Sistema"}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-500">Ofertas ({audit.offers.length})</div>
                <div className="max-h-56 space-y-2 overflow-y-auto" data-testid="audit-offers">
                  {audit.offers.length === 0 && (
                    <div className="rounded-xl border border-dashed border-[#27272A] px-3 py-4 text-center text-xs text-gray-500">
                      Aún no se han generado ofertas para este servicio
                    </div>
                  )}
                  {audit.offers.map((o) => (
                    <div key={o.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#1e1e21] bg-[#101012] px-3 py-2 text-xs">
                      <span className="text-gray-200">{o.driver_name || "Conductor"}</span>
                      <span className={`font-bold ${o.status === "PENDING" ? "text-amber-400" : o.status === "ACCEPTED" ? "text-emerald-400" : "text-gray-500"}`}>{o.status}</span>
                      <span className="text-gray-500">{formatDateTime(o.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancelación individual segura */}
      <Dialog open={!!cancelTarget} onOpenChange={(o) => { if (!o) { setCancelTarget(null); setReason(""); setConfirmed(false); } }}>
        <DialogContent className="border-[#27272A] bg-[#141416] text-white" data-testid="stuck-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancelar servicio #{cancelTarget?.id.slice(0, 8)}</DialogTitle>
            <DialogDescription>
              Se registrará en el historial con tu usuario y el motivo. No se borra ningún registro ni oferta. Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motivo obligatorio…"
            className="border-[#27272A] bg-[#0d0d0f]"
            data-testid="cancel-reason-input"
          />
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <Checkbox checked={confirmed} onCheckedChange={(v) => setConfirmed(!!v)} data-testid="cancel-confirm-checkbox" />
            Confirmo que quiero cancelar este servicio
          </label>
          <DialogFooter>
            <Button
              onClick={doCancel}
              disabled={busy || !reason.trim() || !confirmed}
              className="bg-red-500 text-white hover:bg-red-600 disabled:opacity-40"
              data-testid="cancel-submit-button"
            >
              Cancelar servicio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
